import React, { useState, useContext, useEffect, useRef } from "react"
import { FaPlus, FaGamepad, FaSignOutAlt, FaBars, FaTimes, FaUserCog, FaUser, FaUsers, FaChevronDown, FaStar, FaRegStar, FaCog } from 'react-icons/fa'
import PlayWordleModal from "./PlayWordleModal"
import AddScoreModal from "./AddScoreModal"
import AccountSettingsModal from "./AccountSettingsModal"
import GroupSettingsModal from "./groups/GroupSettingsModal"
import ModalOverlay from "./ModalOverlay"
import ScopeContext from "../contexts/ScopeContext"
import CreateGroupModal from "./groups/CreateGroupModal"
import JoinGroupModal from "./groups/JoinGroupModal"
import logo from '../images/logo.png'

const Header = ({ loggedInUser, onLogout }) => {
  const [showPlayWordleModal, setShowPlayWordleModal] = useState(false)
  const [showAddScoreModal, setShowAddScoreModal] = useState(false)
  const [showAccountSettingsModal, setShowAccountSettingsModal] = useState(false)
  const [showGroupSettingsModal, setShowGroupSettingsModal] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showJoinModal, setShowJoinModal] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false)
  const [settingDefault, setSettingDefault] = useState(null)

  const scopeDropdownRef = useRef(null)

  const {
    groups,
    currentScope,
    defaultGroupId,
    isPersonalScope,
    selectPersonalScope,
    selectGroupScope,
    setDefaultScope
  } = useContext(ScopeContext)

  // Close all modals on auth logout
  useEffect(() => {
    const handleLogout = () => {
      setShowPlayWordleModal(false)
      setShowAddScoreModal(false)
      setShowAccountSettingsModal(false)
      setShowGroupSettingsModal(false)
      setShowCreateModal(false)
      setShowJoinModal(false)
      setMobileMenuOpen(false)
      setScopeDropdownOpen(false)
    }
    window.addEventListener('auth:logout', handleLogout)
    return () => window.removeEventListener('auth:logout', handleLogout)
  }, [])

  // Close scope dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scopeDropdownRef.current && !scopeDropdownRef.current.contains(event.target)) {
        setScopeDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const closeAllModals = () => {
    setShowPlayWordleModal(false)
    setShowAddScoreModal(false)
    setShowAccountSettingsModal(false)
    setShowGroupSettingsModal(false)
    setShowCreateModal(false)
    setShowJoinModal(false)
  }

  const handleSetDefault = async (type, groupId = null, e) => {
    e.stopPropagation()
    const key = type === 'personal' ? 'personal' : groupId
    setSettingDefault(key)
    try {
      await setDefaultScope(type, groupId)
    } finally {
      setSettingDefault(null)
    }
  }

  const handleScopeSelect = (type, groupId = null) => {
    if (type === 'personal') {
      selectPersonalScope()
    } else {
      selectGroupScope(groupId)
    }
    setScopeDropdownOpen(false)
  }

  const handleButtonClick = (action) => {
    setMobileMenuOpen(false)
    action()
  }

  const isPersonalDefault = defaultGroupId === null

  return (
    <div id="header">
      {showPlayWordleModal && (
        <>
          <ModalOverlay onClick={closeAllModals} />
          <PlayWordleModal setShowPlayWordleModal={setShowPlayWordleModal} />
        </>
      )}
      {showAddScoreModal && (
        <>
          <ModalOverlay onClick={closeAllModals} />
          <AddScoreModal onClose={() => setShowAddScoreModal(false)} />
        </>
      )}
      {showAccountSettingsModal && (
        <>
          <ModalOverlay onClick={closeAllModals} />
          <AccountSettingsModal onClose={() => setShowAccountSettingsModal(false)} />
        </>
      )}
      {showGroupSettingsModal && currentScope?.group && (
        <>
          <ModalOverlay onClick={closeAllModals} />
          <GroupSettingsModal
            group={currentScope.group}
            onClose={() => setShowGroupSettingsModal(false)}
          />
        </>
      )}
      {showCreateModal && (
        <>
          <ModalOverlay onClick={() => setShowCreateModal(false)} />
          <CreateGroupModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => setShowCreateModal(false)}
          />
        </>
      )}
      {showJoinModal && (
        <>
          <ModalOverlay onClick={() => setShowJoinModal(false)} />
          <JoinGroupModal onClose={() => setShowJoinModal(false)} />
        </>
      )}

      <div className="header-main">
        <div className="header-left">
          <img src={logo} alt="WordleWise Logo" />
        </div>

        {loggedInUser && (
          <button
            className="burger-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <FaTimes className="icon-20px" /> : <FaBars className="icon-20px" />}
          </button>
        )}
      </div>

      {loggedInUser && (
        <div className={`menu ${mobileMenuOpen ? 'open' : ''}`}>
          {/* Viewing Section */}
          <div className="menu-section">
            <div className="menu-section-label">Viewing</div>

            {/* Scope Dropdown */}
            <div className="menu-scope-dropdown" ref={scopeDropdownRef}>
              <button
                className="menu-scope-trigger"
                onClick={() => setScopeDropdownOpen(!scopeDropdownOpen)}
              >
                {isPersonalScope ? <FaUser size={11} /> : <FaUsers size={12} />}
                <span className="menu-scope-value">
                  {isPersonalScope ? 'Just me' : (currentScope?.group?.name || 'Group')}
                </span>
                <FaChevronDown size={9} className="menu-scope-arrow" />
              </button>

              {scopeDropdownOpen && (
                <div className="menu-scope-options">
                  <div
                    className={`menu-scope-item ${isPersonalScope ? 'selected' : ''}`}
                    onClick={() => handleScopeSelect('personal')}
                  >
                    <FaUser size={12} className="menu-scope-item-icon" />
                    <span className="menu-scope-item-text">Just me</span>
                    <button
                      className={`default-star-btn ${isPersonalDefault ? 'is-default' : ''} ${settingDefault === 'personal' ? 'is-loading' : ''}`}
                      onClick={(e) => handleSetDefault('personal', null, e)}
                      disabled={settingDefault !== null}
                      title={isPersonalDefault ? 'Default view' : 'Set as default'}
                    >
                      {isPersonalDefault ? <FaStar size={11} /> : <FaRegStar size={11} />}
                    </button>
                  </div>

                  {groups.map(group => {
                    const isDefault = defaultGroupId === group.id
                    const isSelected = currentScope?.group?.id === group.id
                    return (
                      <div
                        key={group.id}
                        className={`menu-scope-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleScopeSelect('group', group.id)}
                      >
                        <FaUsers size={12} className="menu-scope-item-icon" />
                        <span className="menu-scope-item-text">{group.name}</span>
                        <button
                          className={`default-star-btn ${isDefault ? 'is-default' : ''} ${settingDefault === group.id ? 'is-loading' : ''}`}
                          onClick={(e) => handleSetDefault('group', group.id, e)}
                          disabled={settingDefault !== null}
                          title={isDefault ? 'Default view' : 'Set as default'}
                        >
                          {isDefault ? <FaStar size={11} /> : <FaRegStar size={11} />}
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Group Actions */}
            <div className="menu-group-actions">
              <button
                className="menu-group-btn"
                onClick={() => { setShowCreateModal(true); setMobileMenuOpen(false); }}
              >
                Create group
              </button>
              <button
                className="menu-group-btn"
                onClick={() => { setShowJoinModal(true); setMobileMenuOpen(false); }}
              >
                Join group
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="menu-divider"></div>

          {/* Action Buttons */}
          <div className="menu-content">
            <button
              className="headerButton"
              onClick={() => handleButtonClick(() => setShowAddScoreModal(true))}
            >
              <FaPlus className="icon-20px" />
              <span className="header-button-label">Add score</span>
            </button>

            <button
              className="headerButton"
              onClick={() => handleButtonClick(() => setShowPlayWordleModal(true))}
            >
              <FaGamepad className="icon-20px" />
              <span className="header-button-label">Play Wordle</span>
            </button>

            <button
              className="headerButton"
              onClick={() => handleButtonClick(() => setShowAccountSettingsModal(true))}
            >
              <FaUserCog className="icon-20px" />
              <span className="header-button-label">Account settings</span>
            </button>

            {!isPersonalScope && (
              <button
                className="headerButton"
                onClick={() => handleButtonClick(() => setShowGroupSettingsModal(true))}
              >
                <FaCog className="icon-20px" />
                <span className="header-button-label">Group settings</span>
              </button>
            )}

            <button
              className="headerButton"
              onClick={() => handleButtonClick(onLogout)}
            >
              <FaSignOutAlt className="icon-20px" />
              <span className="header-button-label">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Header;